<template>
    <DataTable
        :header="$t('summary')"
        :fields="fields"
        :items="items"
        :loading="loading"
        :error="request.getError()"
        class="mb-4"
        show-export-button
        :filter="filterSearch"
    >
        <template #headerSide>
            <div :key="spaceId" class="d-flex gap-3">
                <FormCategoryTreeSelect
                    ref="FormCategoryTreeSelect"
                    :value="categories"
                    :disabled="loading"
                    class="width-200"
                    preselect
                    prevent-empty-selection
                    :clearable="false"
                    @change="categories = $event"
                    @loading="categoriesLoading = $event"
                />
                <b-form-input v-model="filterSearch" :placeholder="$t('search')" :disabled="loading" class="width-200" />
            </div>
        </template>
        <template #cell(impressions)="{item}">
            <div>{{ item.impressions }}</div>
            <div><Difference :difference="$format.percent(item.impressionsDiff)" /></div>
        </template>
        <template #cell(clicks)="{item}">
            <div>{{ item.clicks }}</div>
            <div><Difference :difference="$format.percent(item.clicksDiff)" /></div>
        </template>
        <template #cell(cr)="{item}">
            <div>{{ $format.percent(item.cr) }}</div>
            <div><Difference :difference="$format.percent(item.crDiff)" /></div>
        </template>
        <template #cell(quantity)="{item}">
            <div>{{ item.quantity }}</div>
            <div><Difference :difference="$format.percent(item.quantityDiff)" /></div>
        </template>
        <template #cell(amount)="{item}">
            <div>{{ $format.currency(item.amount) }}</div>
            <div><Difference :difference="$format.percent(item.amountDiff)" /></div>
        </template>
    </DataTable>
</template>



<script>
    import sumBy from 'lodash/sumBy';
    import { calcPercentDiff } from '@/utils/number';
    import DataTable from '@/components/common/DataTable.vue';
    import Difference from '@/components/common/Difference.vue';
    import FormCategoryTreeSelect from '@/components/common/FormCategoryTreeSelect.vue';

    export default {
        name: 'ProductReports',
        components: {
            DataTable,
            Difference,
            FormCategoryTreeSelect,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                request: this.$req('space/ProductReports'),
                categories: [],
                filterSearch: '',
                categoriesLoading: false,
            };
        },
        computed: {
            loadingFilters() {
                return this.categoriesLoading;
            },
            loading() {
                return this.request.getLoading() || this.loadingFilters;
            },
            fields() {
                return [
                    {
                        key: 'name',
                        label: this.$t('product'),
                        sortable: true,
                        exportable: true,
                        tdClass: '',
                        thClass: '',
                    },
                    {
                        key: 'sku',
                        label: this.$t('model'),
                        sortable: true,
                        exportable: true,
                        tdClass: '',
                        thClass: '',
                    },
                    {
                        key: 'impressions',
                        label: this.$t('impressions'),
                        sortable: true,
                        total: true,
                        formatter: val => this.$format.number(val),
                        exportable: true,
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                    {
                        key: 'impressionsDiff',
                        label: `${this.$t('impressions')} (% ${this.$t('change2').toLowerCase()})`,
                        sortable: true,
                        exportable: true,
                        show: false,
                        formatter: val => this.$format.number(val),
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                    {
                        key: 'clicks',
                        label: this.$t('clicks'),
                        sortable: true,
                        total: true,
                        formatter: val => this.$format.number(val),
                        exportable: true,
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                    {
                        key: 'clicksDiff',
                        label: `${this.$t('clicks')} (% ${this.$t('change2').toLowerCase()})`,
                        sortable: true,
                        exportable: true,
                        show: false,
                        formatter: val => this.$format.number(val),
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                    {
                        key: 'quantity',
                        label: this.$t('quantity'),
                        exportLabel: this.$t('quantity2'),
                        sortable: true,
                        total: true,
                        formatter: val => this.$format.number(val),
                        exportable: true,
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                    {
                        key: 'quantityDiff',
                        label: `${this.$t('quantity')} (% ${this.$t('change2').toLowerCase()})`,
                        exportLabel: `${this.$t('quantity2')} (% ${this.$t('change2').toLowerCase()})`,
                        sortable: true,
                        exportable: true,
                        show: false,
                        formatter: val => this.$format.number(val),
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                    {
                        key: 'cr',
                        label: this.$t('cr'),
                        sortable: true,
                        total: true,
                        formatter: val => this.$format.number(val),
                        totalFormatter: val => this.$format.percent(val),
                        totalValue: (item, tableItems) => (sumBy(tableItems, 'quantity') / sumBy(tableItems, 'clicks')) * 100 || 0,
                        exportable: true,
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                    {
                        key: 'crDiff',
                        label: `${this.$t('cr')} (% ${this.$t('change2').toLowerCase()})`,
                        sortable: true,
                        exportable: true,
                        show: false,
                        formatter: val => this.$format.number(val),
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                    {
                        key: 'amount',
                        label: this.$t('amount'),
                        sortable: true,
                        total: true,
                        formatter: val => this.$format.number(val),
                        totalFormatter: val => this.$format.currency(val),
                        exportable: true,
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                    {
                        key: 'amountDiff',
                        label: `${this.$t('amount')} (% ${this.$t('change2').toLowerCase()})`,
                        sortable: true,
                        exportable: true,
                        show: false,
                        formatter: val => this.$format.number(val),
                        tdClass: 'text-right text-nowrap',
                        thClass: 'text-right',
                    },
                ];
            },
            items() {
                return (this.request.getData() || []).map(item => ({
                    ...item,
                    cr: (item.quantity / item.clicks) * 100 || 0,
                    crDiff: calcPercentDiff((item.quantity / item.clicks) * 100, (item.quantity_compare / item.clicks_compare) * 100),
                    impressionsDiff: calcPercentDiff(item.impressions, item.compareImpressions),
                    clicksDiff: calcPercentDiff(item.clicks, item.compareClicks),
                    quantityDiff: calcPercentDiff(item.quantity, item.compareQuantity),
                    amountDiff: calcPercentDiff(item.amount, item.compareAmount),
                }));
            },
            spaceId() {
                return this.$store.state.space.currentSpaceId;
            },
            timeRange() {
                return this.parentComponent.timeRange;
            },
            apiParams() {
                const urlParams = { spaceId: this.spaceId };
                const { startDate, endDate, compareStartDate, compareEndDate } = this.timeRange;
                const requestParams = { startDate, endDate, compareStartDate, compareEndDate };
                if (this.categories?.length) requestParams.categories = this.categories.join(',');
                return { urlParams, requestParams };
            },
        },
        watch: {
            spaceId() {
                this.resetFilters();
            },
            apiParams: {
                immediate: true,
                handler(newVal, oldVal) {
                    if (JSON.stringify(newVal || {}) !== JSON.stringify(oldVal || {})) {
                        this.loadData();
                    }
                },
            },
        },
        methods: {
            calcPercentDiff,
            loadData() {
                if (!this.loadingFilters) {
                    this.resetData();
                    this.fetchData();
                }
            },
            resetData() {
                this.request.abortAll();
                this.request.resetAll();
            },
            fetchData() {
                this.request.get(this.apiParams);
            },
            resetFilters() {
                this.categories = [];
                this.filterSearch = '';
                this.categoriesLoading = false;
            },
        },
        created() {
            this.parentComponent.$on('resetFilters', this.resetFilters);
        },
        beforeDestroy() {
            this.parentComponent.$off('resetFilters', this.resetFilters);
        },
    };
</script>
